import { useSEO } from "@/hooks/use-seo";
import { ChevronLeft } from "lucide-react";

export default function CookiePolicy() {
  useSEO({
    title: "Cookie Policy — Ayrshire Hub",
    description: "How Ayrshire Hub uses cookies and local storage.",
    noIndex: false,
  });

  const updated = "12 May 2026";

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <button onClick={() => window.history.back()} className="flex items-center gap-1 text-sm text-slate-500 hover:text-primary transition-colors mb-6 -ml-1">
        <ChevronLeft size={18} /> Back
      </button>
      <h1 className="text-3xl font-bold text-primary mb-2">Cookie Policy</h1>
      <p className="text-slate-500 text-sm mb-8">Last updated: {updated}</p>

      <div className="prose prose-slate max-w-none space-y-8 text-slate-700 leading-relaxed">

        <section>
          <h2 className="text-xl font-semibold text-primary mb-3">1. What are cookies?</h2>
          <p>
            Cookies are small text files stored on your device by your browser when you visit a website.
            Ayrshire Hub also uses browser <strong>localStorage</strong>, which works similarly but is not sent
            with every network request.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-primary mb-3">2. What we use</h2>

          <h3 className="text-base font-semibold text-slate-800 mt-3 mb-2">Essential storage (cannot be disabled)</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-slate-100">
                  <th className="text-left p-3 border border-slate-200 font-semibold">Name</th>
                  <th className="text-left p-3 border border-slate-200 font-semibold">Type</th>
                  <th className="text-left p-3 border border-slate-200 font-semibold">Purpose</th>
                  <th className="text-left p-3 border border-slate-200 font-semibold">Expires</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["ayrshire_token", "localStorage", "Stores your authentication token to keep you logged in", "Until logout"],
                  ["ayrshire_town", "localStorage", "Remembers your chosen town for local content filtering", "Until cleared"],
                  ["ayrshire_cookie_consent", "localStorage", "Records your cookie consent preference", "1 year"],
                ].map(([name, type, purpose, expires]) => (
                  <tr key={name} className="even:bg-slate-50">
                    <td className="p-3 border border-slate-200 font-mono text-xs">{name}</td>
                    <td className="p-3 border border-slate-200">{type}</td>
                    <td className="p-3 border border-slate-200">{purpose}</td>
                    <td className="p-3 border border-slate-200">{expires}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3 className="text-base font-semibold text-slate-800 mt-5 mb-2">Third-party services</h3>
          <p>
            When you make a payment, Stripe may set their own cookies on their payment pages. These are governed
            by{" "}
            <a href="https://stripe.com/gb/privacy" target="_blank" rel="noopener noreferrer" className="text-primary underline">
              Stripe's Privacy Policy
            </a>.
          </p>
          <p className="mt-2">
            We do <strong>not</strong> use any analytics, advertising, or tracking cookies.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-primary mb-3">3. How to control cookies</h2>
          <p>
            You can clear cookies and localStorage at any time through your browser settings. Note that clearing
            authentication storage will log you out of Ayrshire Hub.
          </p>
          <p className="mt-2">Browser guides for managing cookies:</p>
          <ul className="list-disc pl-5 space-y-1 mt-1">
            <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-primary underline">Google Chrome</a></li>
            <li><a href="https://support.mozilla.org/en-US/kb/clear-cookies-and-site-data-firefox" target="_blank" rel="noopener noreferrer" className="text-primary underline">Mozilla Firefox</a></li>
            <li><a href="https://support.apple.com/en-gb/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-primary underline">Apple Safari</a></li>
            <li><a href="https://support.microsoft.com/en-gb/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-primary underline">Microsoft Edge</a></li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-primary mb-3">4. Changes to this policy</h2>
          <p>
            We may update this Cookie Policy if we introduce new features. We will update the date at the top
            of this page and, where appropriate, notify registered users by email.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-primary mb-3">5. More information</h2>
          <p>
            Ayrshire Hub is operated by <strong>Scotdev Ltd</strong> (Company No. SC887365), registered in Scotland.
            For more about how we use your personal data, see our{" "}
            <a href="/privacy" className="text-primary underline">Privacy Policy</a>. For questions, email{" "}
            <a href="mailto:privacy@ayrshirehub.co.uk" className="text-primary underline">privacy@ayrshirehub.co.uk</a>.
          </p>
        </section>

      </div>
    </div>
  );
}
